import React from 'react';
import { Tooltip } from 'antd';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { HolderOutlined, PrinterOutlined, ThunderboltOutlined } from '@ant-design/icons';
import zoneStore from '../../Store';
import queueStore from '../../Store/queueStore';
import selectionStore, { selectButton, selectScreen } from '../../Store/selectionStore';
import { describeButtonTarget, isBrokenTarget } from '../../../utils/flowAnalysis';
import { attachIcon } from '../../../utils/buttonIcon';
import ButtonRow from '../ButtonRow';
import {
  Branch, CardSlice, ChildSlot, Connector, EmptyRow, HeaderName, HeaderTitle,
  Leaf, LeafBody, LeafKind, NodeBox, NodeFooter, NodeHeader, PassRow,
} from './style';

// Экран, на который ведёт кнопка. У внешней интеграции продолжение лежит в отдельном поле.
const targetScreenId = (button) => (
  button.path === 'externalIntegration' ? button.nextScreen : button.path
);

function ScreenNode({ screen, ancestors }) {
  const screens = zoneStore.useState((s) => s.config.screens);
  const queues = queueStore.useState((s) => s.queue) || [];
  const selected = selectionStore.useState((s) => s);

  const isSelected = selected.screenId === screen.screenId;

  const renderChild = (targetId) => {
    const target = screens.find((item) => item.screenId === targetId);
    if (!target) return null;

    // Возврат на экран, через который мы сюда пришли, — цикл: дальше не разворачиваем
    if (ancestors.includes(targetId)) {
      return (
        <Leaf>
          <LeafBody>
            <LeafKind>цикл</LeafKind>
            <div>{`↺ снова «${target.headTitle || target.screenId}»`}</div>
          </LeafBody>
        </Leaf>
      );
    }

    return <ScreenNode screen={target} ancestors={[...ancestors, screen.screenId]} />;
  };

  // Ветка кончается не экраном, а действием терминала — показываем это отдельным узлом,
  // иначе «печать талона» выглядит как подпись, а не как конец потока
  const renderTerminal = (target) => {
    if (target.kind === 'queue') {
      return (
        <Leaf>
          <PrinterOutlined style={{ fontSize: 18, color: '#8c8c8c' }} />
          <LeafBody>
            <LeafKind>печать талона</LeafKind>
            <div>{target.label.replace('очередь ', '')}</div>
          </LeafBody>
        </Leaf>
      );
    }

    if (target.kind === 'action') {
      return (
        <Leaf>
          <ThunderboltOutlined style={{ fontSize: 16, color: '#8c8c8c' }} />
          <LeafBody>
            <LeafKind>действие терминала</LeafKind>
            <div>{target.label}</div>
          </LeafBody>
        </Leaf>
      );
    }

    if (isBrokenTarget(target) && target.kind !== 'empty') {
      return (
        <Leaf $broken>
          <LeafBody>
            <LeafKind>не настроено</LeafKind>
            <div>{target.label}</div>
          </LeafBody>
        </Leaf>
      );
    }

    return null;
  };

  const buttons = screen.buttons || [];
  // Нижний блок — отдельный набор кнопок экрана; на схеме он тоже часть потока
  const bottomButtons = screen.bottomBlock?.buttons || [];

  const buttonRow = (button, keyPrefix = '', dragHandle = null) => {
    const target = describeButtonTarget(button, screens, queues);
    const child = renderChild(targetScreenId(button)) || renderTerminal(target);

    return {
      key: `${keyPrefix}${button.id}`,
      // Куда ведёт — всегда справа от строки, поэтому в самой строке это не повторяем
      content: (
        <ButtonRow
          name={button.name}
          extraText={button.extraText || null}
          targetLabel={target.kind === 'empty' ? target.label : null}
          icon={button.icon || null}
          isBroken={isBrokenTarget(target)}
          isActive={selected.buttonId === button.id}
          onClick={() => selectButton(screen.screenId, button.id)}
          handle={dragHandle}
          onIconDrop={(file) => attachIcon(screen.screenId, button, file)}
        />
      ),
      child,
    };
  };

  const branch = (item, index, extraProps = {}, innerRef = null, dragProps = {}) => (
    <Branch key={item.key} ref={innerRef} {...dragProps} {...extraProps}>
      <CardSlice $selected={isSelected} $first={index === 0}>
        {item.content}
      </CardSlice>
      {item.child && <Connector>→</Connector>}
      {item.child && <ChildSlot>{item.child}</ChildSlot>}
    </Branch>
  );

  const headRows = [];
  const tailRows = [];

  if (screen.screenPath) {
    headRows.push({
      key: 'pass',
      content: <PassRow>после заполнения экрана →</PassRow>,
      child: renderChild(screen.screenPath),
    });
  }

  if (bottomButtons.length > 0) {
    tailRows.push({
      key: 'bottom-title',
      content: <EmptyRow>нижний блок — правится в старом редакторе</EmptyRow>,
      child: null,
    });
    bottomButtons.forEach((button) => tailRows.push(buttonRow(button, 'bottom-')));
  }

  if (headRows.length === 0 && tailRows.length === 0 && buttons.length === 0) {
    tailRows.push({ key: 'empty', content: <EmptyRow>кнопок нет</EmptyRow>, child: null });
  }

  return (
    <NodeBox>
      <Tooltip
        title={(
          <>
            {screen.title && <div>{`Заголовок: ${screen.title}`}</div>}
            {screen.description && <div>{`Подзаголовок: ${screen.description}`}</div>}
            {!screen.title && !screen.description && 'Тексты для клиента не заданы'}
          </>
        )}
        placement="top"
      >
        <NodeHeader $selected={isSelected} onClick={() => selectScreen(screen.screenId)}>
          <HeaderName>
            {`${screen.isStart ? '★ ' : ''}${screen.headTitle || screen.title || screen.screenId}`}
          </HeaderName>
          {screen.title && <HeaderTitle>{screen.title}</HeaderTitle>}
        </NodeHeader>
      </Tooltip>

      {headRows.map((item, index) => branch(item, index))}

      <Droppable
        droppableId={`scheme-${screen.screenId}`}
        type={`buttons-${screen.screenId}`}
      >
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {buttons.map((button, index) => (
              <Draggable draggableId={`scheme-${button.id}`} index={index} key={button.id}>
                {(dragProvided) => branch(
                  buttonRow(
                    button,
                    '',
                    <span {...dragProvided.dragHandleProps} style={{ color: '#bfbfbf' }}>
                      <HolderOutlined />
                    </span>,
                  ),
                  headRows.length === 0 && index === 0 ? 0 : 1,
                  { style: dragProvided.draggableProps.style },
                  dragProvided.innerRef,
                  dragProvided.draggableProps,
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      {tailRows.map((item) => branch(item, 1))}

      <NodeFooter $selected={isSelected} />
    </NodeBox>
  );
}

export default ScreenNode;
